import { useSelector } from '@xstate/react';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigationType, useSearchParams } from 'react-router';
import { UserContext } from '~/app';
import { useMarketContractsRef } from '~/features/market/marketSelectors';
import { isNotificationArrival } from '~/features/notifications/constants';
import { parsePositiveIntParam } from '~/features/shared/deepLinkParams';
import { FilterTab } from '../types';

const FILTER_TABS: readonly FilterTab[] = ['active', 'upcoming', 'past'];

export function useContractsService() {
  const {
    loading: parentLoading,
    marketContractsRef: contractsRef,
    employerUser,
  } = useMarketContractsRef();
  // [MCV-1727] The contracts page serves both roles. Role detection follows the
  // pattern the other shared HLX routes use (HlxCalendarRoute, HlxListingsRoute,
  // JobOpportunitiesRoute, ProtectedHLXRoute).
  const isProviderView = UserContext.useSelector((state) =>
    state.matches({ $_AUTHENTICATED: '$_AUTHENTICATED_PROVIDER' })
  );
  const [params, setParams] = useSearchParams();
  const rawContractId = params.get('contractId');
  const contractId = parsePositiveIntParam(params.toString(), 'contractId') ?? null;
  const invalidLink = rawContractId !== null && contractId === null;

  // [MCV-1753] A notification click on the contract already open changes neither `contractId` nor
  // the path, so nothing re-read it and the page kept showing the contract as it was before the
  // change that raised the alert. The key of that click re-runs the open below. It is latched: it
  // only ever moves to the key of a NEW click, so a later plain navigation does not re-open the
  // contract (a POP is never a click, see `isNotificationArrival`).
  const location = useLocation();
  const navigationType = useNavigationType();
  const arrivalKey = isNotificationArrival(location.state, navigationType) ? location.key : null;
  const [notificationOpenKey, setNotificationOpenKey] = useState(arrivalKey);
  if (arrivalKey !== null && arrivalKey !== notificationOpenKey) {
    setNotificationOpenKey(arrivalKey);
  }

  useEffect(() => {
    if (contractId === null || !contractsRef) return;
    contractsRef.send({ type: 'ON_OPEN_CONTRACT', payload: { id: contractId } });
    return () => {
      contractsRef.send({ type: 'ON_CLEAR_CONTRACT_LINK' });
    };
  }, [contractId, contractsRef, notificationOpenKey]);

  // [MCV-1954] `?tab=` (e.g. from the contract-ready notice, whose new contract is PENDING and so
  // only on Upcoming) picks the tab once on arrival, then leaves the URL so a later tab click is
  // not overridden. A `contractId` link picks its own tab, so it wins.
  const rawTab = params.get('tab');
  useEffect(() => {
    if (rawTab === null || !contractsRef) return;
    if (contractId === null && FILTER_TABS.includes(rawTab as FilterTab)) {
      contractsRef.send({
        type: 'ON_FILTER_TAB',
        payload: { tab: rawTab as FilterTab, clearFilters: true },
      });
    }
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete('tab');
        return next;
      },
      { replace: true }
    );
  }, [rawTab, contractId, contractsRef, setParams]);

  const clearLink = useCallback(() => {
    if (rawContractId === null) return;
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete('contractId');
        return next;
      },
      { replace: true }
    );
  }, [rawContractId, setParams]);

  // Narrow child selectors — each field subscribes independently
  const loading = useSelector(contractsRef, (s) => s?.context.loading) ?? parentLoading;
  const contracts = useSelector(contractsRef, (s) => s?.context.contracts) ?? [];
  const selectedContract = useSelector(contractsRef, (s) => s?.context.selectedContract) ?? null;
  const filterTab = useSelector(contractsRef, (s) => s?.context.filterTab) ?? 'active';
  const roleFilter = useSelector(contractsRef, (s) => s?.context.roleFilter) ?? null;
  const search = useSelector(contractsRef, (s) => s?.context.search) ?? '';
  const detailError = useSelector(contractsRef, (s) => s?.context.detailError) ?? null;
  const signError = useSelector(contractsRef, (s) => s?.context.signError) ?? null;
  // [MCV-1829] Busy from the sign request through the reload that follows it: the
  // dialog stays mounted until the reloaded contract shows the signature, and the
  // button must not re-enable in between (a second post would only 409). ON_SIGN is
  // accepted only in $_VIEW, so "not in $_VIEW" covers $_SIGN and both reload paths.
  // `context.loading` is not enough: the unlinked $_LOAD path resets it after the
  // list loads, before the detail fetch that shows the signature.
  // [MCV-1906] A Load more page fetch is not busy for the detail pane: it takes a
  // sign from there as $_VIEW does.
  const signing =
    useSelector(contractsRef, (s) => !!s && !s.matches('$_VIEW') && !s.matches('$_LOAD_MORE')) ??
    false;
  const linkedContractId = useSelector(contractsRef, (s) => s?.context.linkedContractId) ?? null;
  // [MCV-1906] More pages exist when the last one came back full; `loadingMore` is the
  // state that fetches the next one (the list itself stays on screen meanwhile).
  const hasMore = useSelector(contractsRef, (s) => s?.context.hasMore) ?? false;
  const loadingMore = useSelector(contractsRef, (s) => !!s && s.matches('$_LOAD_MORE')) ?? false;

  // Deliberate list interactions end the URL selection so it cannot override
  // a new selection or filter, including after a page refresh.
  const handleFilterTab = useCallback(
    (tab: FilterTab) => {
      clearLink();
      contractsRef?.send({ type: 'ON_FILTER_TAB', payload: { tab } });
    },
    [contractsRef, clearLink]
  );
  const handleSearch = useCallback(
    (value: string) => {
      clearLink();
      contractsRef?.send({ type: 'ON_SEARCH', payload: { search: value } });
    },
    [contractsRef, clearLink]
  );
  const handleRoleToggle = useCallback(
    (role: 'HIRING' | 'HIRED' | null) => {
      clearLink();
      contractsRef?.send({ type: 'ON_ROLE_TOGGLE', payload: { role } });
    },
    [contractsRef, clearLink]
  );
  const handleSelect = useCallback(
    (id: number) => {
      clearLink();
      contractsRef?.send({ type: 'ON_SELECT', payload: { id } });
    },
    [contractsRef, clearLink]
  );
  const handleLoadMore = useCallback(
    () => contractsRef?.send({ type: 'ON_LOAD_MORE' }),
    [contractsRef]
  );
  const handleSign = useCallback(
    (signerName: string, consentAccepted: boolean) =>
      contractsRef?.send({ type: 'ON_SIGN', payload: { signerName, consentAccepted } }),
    [contractsRef]
  );
  const handleRequestTermination = useCallback(
    (reason: string) => contractsRef?.send({ type: 'ON_REQUEST_TERMINATION', payload: { reason } }),
    [contractsRef]
  );

  const handleWithdrawTermination = useCallback(
    () => contractsRef?.send({ type: 'ON_WITHDRAW_TERMINATION' }),
    [contractsRef]
  );

  return {
    contractsRef,
    employerUser,
    isProviderView,
    loading,
    contracts,
    selectedContract:
      invalidLink || (contractId !== null && selectedContract?.id !== contractId)
        ? null
        : selectedContract,
    detailError: invalidLink
      ? 'This contract link is invalid. Please select a contract from the list.'
      : contractId !== null && linkedContractId !== contractId
        ? null
        : detailError,
    // Only the first open of a link shows the spinner; later reloads (sign,
    // termination) keep the loaded detail on screen.
    openingLinkedContract:
      contractId !== null &&
      (linkedContractId !== contractId || selectedContract?.id !== contractId),
    filterTab,
    roleFilter,
    search,
    hasMore,
    loadingMore,
    signError,
    signing,
    handleFilterTab,
    handleSearch,
    handleRoleToggle,
    handleSelect,
    handleLoadMore,
    handleSign,
    handleRequestTermination,
    handleWithdrawTermination,
  };
}
